import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { extractDocsFromPdf, parseDocumentationStream } from './apiIntegrationService';
import { IntegrationDetail } from './apiIntegrations.types';
import './apiintegrations.css';

interface Props {
  /** The draft being edited, if any - its saved docs are restored and a parse updates it in place. */
  existing: IntegrationDetail | null;
  onParsed: (detail: IntegrationDetail, warnings: string[]) => void;
  onBack: () => void;
  /** Continue to Endpoints without parsing again (only offered for an existing draft). */
  onNext: () => void;
  onDirtyChange: (dirty: boolean) => void;
}

const ImportStep: React.FC<Props> = ({ existing, onParsed, onBack, onNext, onDirtyChange }) => {
  const savedDocs = existing?.integration.source_raw_docs ?? '';
  const [rawDocs, setRawDocs] = useState(savedDocs);
  const [nameHint, setNameHint] = useState(existing?.integration.name ?? '');
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [rawPreview, setRawPreview] = useState('');
  const previewRef = useRef<HTMLPreElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importNote, setImportNote] = useState<{ warn: boolean; text: string } | null>(null);
  const busy = parsing || importing;

  const importPdf = async (file: File) => {
    if (rawDocs.trim() && !window.confirm('Replace the documentation above with the text from this PDF?')) return;
    setImporting(true);
    setError(null);
    setImportNote(null);
    try {
      const docs = await extractDocsFromPdf(file);
      setRawDocs(docs.text);
      const pages = `${docs.pages} page${docs.pages === 1 ? '' : 's'}`;
      setImportNote(docs.chars > docs.max_chars
        ? { warn: true, text: `Imported ${docs.filename} (${pages}), but it's ${docs.chars.toLocaleString()} characters - the limit is ${docs.max_chars.toLocaleString()}. Trim it to the API reference before parsing.` }
        : { warn: false, text: `Imported ${docs.filename} (${pages}). Check the text below, then parse it.` });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Couldn't import that PDF.");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    onDirtyChange(rawDocs.trim() !== savedDocs.trim());
  }, [rawDocs, savedDocs, onDirtyChange]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const handleParse = async () => {
    if (!rawDocs.trim()) return;
    if (existing && !window.confirm('Parsing again replaces this integration\'s endpoints with what\'s found in the docs. Continue?')) {
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setParsing(true);
    setError(null);
    setStatus('Starting…');
    setRawPreview('');
    let finished = false;
    try {
      await parseDocumentationStream(rawDocs.trim(), nameHint.trim() || undefined, event => {
        if (event.type === 'progress') {
          setStatus(event.status);
          if (event.raw) setRawPreview(event.raw);
          // Keep the newest tokens in view.
          requestAnimationFrame(() => {
            if (previewRef.current) previewRef.current.scrollTop = previewRef.current.scrollHeight;
          });
        } else if (event.type === 'done') {
          finished = true;
          onParsed(
            { integration: event.integration, endpoints: event.endpoints },
            event.parse_warnings ?? event.integration.parse_warnings ?? [],
          );
        } else if (event.type === 'error') {
          finished = true;
          setError(event.message);
        }
      }, controller.signal, existing?.integration.id);
      if (!finished && !controller.signal.aborted) {
        setError('The connection closed before parsing finished. Please try again.');
      }
    } catch (err: unknown) {
      if (!controller.signal.aborted) {
        setError(err instanceof Error ? err.message : 'Parsing failed. Please try again.');
      }
    } finally {
      abortRef.current = null;
      setParsing(false);
    }
  };

  const cancelParse = () => {
    abortRef.current?.abort();
    setStatus(null);
    setError('Parsing cancelled. Nothing was saved.');
  };

  return (
    <motion.div className="mt-step-content" key="integration-import"
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.22 }}>
      <h2 className="mt-step-title">{existing ? 'API documentation' : 'Paste API documentation'}</h2>
      <p className="mt-step-subtitle">
        Paste an OpenAPI/Swagger spec, a Postman collection, or plain-text API docs, or import a PDF. We'll find
        the base URL, authentication and endpoints for you to review.
      </p>

      <div className="mt-field mt-field--full">
        <label className="mt-label" htmlFor="integration-name-hint">Name</label>
        <input id="integration-name-hint" className="mt-input" placeholder="e.g. Acme CRM"
          value={nameHint} onChange={e => setNameHint(e.target.value)} disabled={busy} />
        <p className="mt-field-hint">Optional - you can change it on the Info step.</p>
      </div>

      <div className="mt-field mt-field--full">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <label className="mt-label" htmlFor="integration-raw-docs">Documentation <span className="mt-required">*</span></label>
          <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" disabled={busy} onClick={() => fileInputRef.current?.click()}>
            {importing ? 'Importing…' : 'Import a PDF'}
          </button>
          <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" hidden
            onChange={e => { const f = e.target.files?.[0]; if (f) importPdf(f); }} />
        </div>
        <textarea id="integration-raw-docs" className="mt-textarea ai-docs-textarea"
          placeholder="Paste your API documentation here, or import a PDF..."
          value={rawDocs} onChange={e => setRawDocs(e.target.value)} disabled={busy} />
        {importNote && <p className={importNote.warn ? 'mt-field-hint ai-warn-text' : 'mt-field-hint'}>{importNote.text}</p>}
      </div>

      {error && <p className="ai-error" role="alert">{error}</p>}

      {parsing ? (
        <div style={{ marginBottom: 8 }}>
          <p className="ai-progress"><span className="ai-spinner" aria-hidden="true" />{status ?? 'Working…'}</p>
          <pre ref={previewRef} className="ai-code-box" style={{ maxHeight: 220, fontSize: 11.5 }}>
            {rawPreview || 'Waiting for the model to start responding…'}
          </pre>
        </div>
      ) : (
        <p className="ai-hint">Parsing can take a minute or two for large documentation.</p>
      )}

      <div className="mt-actions">
        <button type="button" className="mt-btn mt-btn--secondary" onClick={onBack} disabled={busy}>← Back</button>
        {parsing ? (
          <button type="button" className="mt-btn mt-btn--secondary mt-btn--danger-outline" onClick={cancelParse}>Cancel parsing</button>
        ) : existing ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="mt-btn mt-btn--secondary" disabled={!rawDocs.trim() || importing} onClick={handleParse}>Parse again</button>
            <button type="button" className="mt-btn mt-btn--primary" onClick={onNext}>Next →</button>
          </div>
        ) : (
          <button type="button" className="mt-btn mt-btn--primary" disabled={!rawDocs.trim() || importing} onClick={handleParse}>
            Parse documentation →
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default ImportStep;
